import React, { useState, useEffect } from 'react';
import { X, Mic, MicOff, Video, VideoOff, PhoneOff, Monitor, MonitorOff } from 'lucide-react';
import {
  LiveKitRoom,
  VideoConference,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  ControlBar,
  useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import api from '../services/api';

interface VideoCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomName: string;
  callId?: number;
  participantName?: string;
  isInitiator?: boolean;
  callType?: 'audio' | 'video';
}

const VideoCallModal: React.FC<VideoCallModalProps> = ({
  isOpen,
  onClose,
  roomName,
  callId,
  participantName,
  isInitiator = false,
  callType = 'video',
}) => {
  const [token, setToken] = useState<string>('');
  const [wsUrl, setWsUrl] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen && roomName) {
      fetchToken();
    }
  }, [isOpen, roomName]);

  // Listen for call_ended and participant_left events
  useEffect(() => {
    if (!isOpen || !callId) return;

    const handleCallEnded = (event: any) => {
      console.log('[VideoCall] Call ended event received:', event.detail);
      if (Number(event.detail?.callId) === callId) {
        onClose();
      }
    };

    const handleParticipantLeft = (event: any) => {
      console.log('[VideoCall] Participant left event received:', event.detail);
      if (Number(event.detail?.callId) === callId && event.detail?.isGroupCall) {
        setTimeout(() => onClose(), 2000);
      }
    };

    window.addEventListener('call_ended', handleCallEnded as EventListener);
    window.addEventListener('participant_left', handleParticipantLeft as EventListener);

    return () => {
      window.removeEventListener('call_ended', handleCallEnded as EventListener);
      window.removeEventListener('participant_left', handleParticipantLeft as EventListener);
    };
  }, [isOpen, callId, onClose]);

  const fetchToken = async () => {
    try {
      setIsConnecting(true);
      setError('');

      const response = await api.post('/calls/token', {
        roomName,
        participantName: participantName || localStorage.getItem('username') || 'Guest',
      });

      if (response && response.token && response.wsUrl) {
        setToken(response.token);
        setWsUrl(response.wsUrl);
        setIsConnecting(false);
      } else {
        throw new Error('Token veya WebSocket URL alınamadı');
      }
    } catch (err: any) {
      console.error('Token fetch error:', err);
      setError(err?.message || 'Görüşme başlatılamadı');
      setIsConnecting(false);
    }
  };

  const handleEndCall = async () => {
    if (callId) {
      try {
        await api.post(`/calls/${callId}/end`);
      } catch (error) {
        console.error('Error ending call:', error);
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative w-full h-full max-w-7xl max-h-screen bg-gray-900 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/50 to-transparent">
          <div className="text-white">
            <h2 className="text-xl font-semibold">Görüntülü Görüşme</h2>
            <p className="text-sm text-gray-300">Oda: {roomName}</p>
          </div>
          <button
            onClick={handleEndCall}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <PhoneOff className="w-5 h-5" />
            Görüşmeyi Bitir
          </button>
        </div>

        {/* Main Content */}
        <div className="w-full h-full">
          {isConnecting && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
                <p>Bağlanıyor...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-white bg-red-600/20 p-8 rounded-lg">
                <X className="w-16 h-16 mx-auto mb-4" />
                <p className="text-xl font-semibold mb-2">Hata</p>
                <p>{error}</p>
                <button
                  onClick={onClose}
                  className="mt-4 px-6 py-2 bg-white text-red-600 rounded-lg hover:bg-gray-100"
                >
                  Kapat
                </button>
              </div>
            </div>
          )}

          {!isConnecting && !error && token && wsUrl && (
            <LiveKitRoom
              video={callType === 'video'}
              audio={true}
              token={token}
              serverUrl={wsUrl}
              connect={true}
              data-lk-theme="default"
              style={{ height: '100%' }}
            >
              <VideoConference />
              <RoomAudioRenderer />
            </LiveKitRoom>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoCallModal;
